
import React from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '@/components/Hero';
import FAQItem from '@/components/FAQItem';
import Testimonials from '@/components/Testimonials';
import StickyBookingButton from '@/components/StickyBookingButton';
import TourStructuredData from '@/components/tours/TourStructuredData';
import SEOMetadata from '@/components/common/SEOMetadata';
import FAQStructuredData from '@/components/common/FAQStructuredData';
import TourOverviewSection from '@/components/tours/financial-district/TourOverviewSection';
import TourStopsSection from '@/components/tours/financial-district/TourStopsSection';
import TourBookingSection from '@/components/tours/financial-district/TourBookingSection';
import OtherToursSection from '@/components/tours/financial-district/OtherToursSection';
import QuickInfoBar from '@/components/tours/financial-district/QuickInfoBar';
import { Link } from 'react-router-dom';
import EventLocalBusinessSchema from '@/components/schema/EventLocalBusinessSchema';

const FinancialDistrict = () => {
  // Tour Overview
  const testimonials = [{
    quote: "The Financial District tour was fascinating! I've walked these streets for years but never knew about the cats who helped build Wall Street. Peggy's knowledge is incredible.",
    author: "Michael T."
  }, {
    quote: "As a history buff and cat lover, this tour was perfect. The stories about postal cats and City Hall felines were both educational and entertaining. Highly recommend!",
    author: "Sarah J."
  }, {
    quote: "I never thought I'd be so captivated by stories of cats from 100 years ago! This tour gives you a completely different perspective on New York's financial district.",
    author: "David L."
  }];

  // Tour stops for "You'll learn about" section
  const tourStops = [
    {
      title: "The Equitable Building",
      description: "The cat who was a mouser in the iconic building that housed finance industry offices"
    },
    {
      title: "P.T. Barnum",
      description: "A genuine cherry-colored cat in the famous showman's American Museum"
    },
    {
      title: "Postal cats",
      description: "Federally employed felines who protected the mail"
    },
    {
      title: "City Hall cats",
      description: "The cat mascots who lived and worked in City Hall, beloved by both civic workers and an adoring public"
    },
    {
      title: "Newspaper Row",
      description: "A newspaper office cat who was a merciless editor, fiercely protected by the paper's newsboys"
    },
    {
      title: "And more!",
      description: "Additional fascinating feline tales from New York's financial heart"
    }
  ];

  // FAQ
  const faqs = [{
    question: "What should I bring on the tour?",
    answer: "Comfortable walking shoes, a water bottle, and an umbrella if rain is expected. Don't forget your camera to capture the charming sights and stories of our cat-filled adventure."
  }, {
    question: "Is the tour suitable for children?",
    answer: "Yes, the tour is family-friendly and suitable for children who can walk for about 2 hours and have an interest in cats and history. The stories are engaging for all ages."
  }, {
    question: "What happens if it rains?",
    answer: "The tour will proceed in light rain, so please bring an umbrella or a raincoat. In case of severe weather, we will reschedule or refund your booking to ensure everyone's safety and comfort."
  }, {
    question: "How much walking is involved?",
    answer: "The Financial District tour covers approximately 1.5 miles over 2 hours, with several stops along the way. The pace is leisurely, but comfortable walking shoes are recommended."
  }, {
    question: "Will we see real cats on the tour?",
    answer: "Our tour focuses on historical cat tales rather than live cat sightings. We enjoy the occasional modern feline who might grace us with their presence in a window or doorway, but we can't guarantee any live cat encounters. The magic of our tour is in the historical storytelling that brings these long-ago feline characters to life!"
  }, {
    question: "Is the tour accessible for people with mobility issues?",
    answer: "The Financial District route is generally accessible, with paved sidewalks and minimal stairs. Please contact us in advance if you have specific mobility concerns, and we'll do our best to accommodate your needs."
  }, {
    question: "Can I bring my own cat?",
    answer: "Absolutely! Cat-friendly adventurers are welcome to join our tour. We love seeing modern felines exploring the same streets where their historical counterparts once roamed. Just ensure your cat is comfortable with city sounds, crowds, and a 1.5-mile walk. We recommend a secure harness or carrier for their safety. Your cat might even become the star of the tour photos! Please email us at catsabouttowntour@gmail.com before booking."
  }];

  // FareHarbor booking link and item ID for Financial District
  const fareHarborItemId = 577800;

  return (
    <div className="min-h-screen pt-20">
      <SEOMetadata
        title="Financial District Historical Cat Tour - Wall Street Cats | Cats About Town"
        description="Discover Wall Street's hidden feline history with our unique Financial District cat walking tour. Learn about postal cats, City Hall felines, and more!"
        keywords={[
          "Financial District cat tour",
          "Wall Street cats",
          "NYC cat history",
          "feline walking tour",
          "City Hall cats",
          "postal cats NYC",
          "financial district history",
          "NYC walking tour",
          "cat lovers tour",
          "historical cats Manhattan"
        ]}
        canonicalUrl="https://catsabouttowntours.com/tours/financial-district"
        ogImage="/lovable-uploads/3a2d9982-d677-4f84-ba71-220788d07367.png"
        ogImageAlt="Financial District Historical Cat Tour - Discover Wall Street's feline history"
      />
      
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://catsabouttowntours.com/"
            }, {
              "@type": "ListItem",
              "position": 2,
              "name": "Tours",
              "item": "https://catsabouttowntours.com/tours"
            }, {
              "@type": "ListItem",
              "position": 3,
              "name": "Financial District Cat Tour",
              "item": "https://catsabouttowntours.com/tours/financial-district"
            }]
          })}
        </script>
      </Helmet>
      
      <TourStructuredData tour={{
        name: 'Financial District Historical Cat Tour',
        description: "Discover Wall Street's hidden feline history with our unique Financial District cat walking tour.",
        image: '/lovable-uploads/3a2d9982-d677-4f84-ba71-220788d07367.png',
        url: 'https://catsabouttowntours.com/tours/financial-district',
        price: '40',
        location: 'Financial District',
        duration: 'PT2H',
        highlights: [
          'City Hall cats',
          'Postal cats history',
          'Wall Street felines',
          'P.T. Barnum museum cats',
          'Newspaper Row cats'
        ]
      }} />
      <EventLocalBusinessSchema
        event={{
          name: 'Financial District Historical Cat Tour',
          description:
            "Discover Wall Street's hidden feline history with our unique Financial District cat walking tour.",
          url: 'https://catsabouttowntours.com/tours/financial-district',
          image: '/lovable-uploads/3a2d9982-d677-4f84-ba71-220788d07367.png',
          startDate: new Date().toISOString(),
          endDate: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
          performer: 'Peggy Gavan',
          priceRange: '$40',
          eventStatus: 'https://schema.org/EventScheduled'
        }}
      />
      
      <FAQStructuredData faqs={faqs} pageTitle="Financial District Cat Tour" />
      
      <Hero
        title="Financial District Historical Cat Tour" 
        subtitle="Discover the historical feline heroes who helped build the neighborhood from the 1800s and beyond" 
        backgroundImage="/lovable-uploads/3a2d9982-d677-4f84-ba71-220788d07367.png"
        height="medium"
        buttonText="Check Availability & Book"
        buttonLink="#book-now"
        showUrgencyMessage={true}
        backgroundAlt="Financial District skyline at dusk"
      />
      
      <QuickInfoBar />
      <TourOverviewSection />
      <TourStopsSection tourStops={tourStops} />
      
      <section className="py-8 bg-neutral-paper relative">
        <Testimonials testimonials={testimonials} />
      </section>
      
      <TourBookingSection fareHarborItemId={fareHarborItemId} />
      
      <section className="py-12 bg-[#F5EDE0]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-playfair font-bold text-2xl md:text-3xl mb-8 text-[#3E2723] text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Link to="/contact" className="text-[#D32F2F] hover:underline font-montserrat">
                Have another question? Contact us
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      <OtherToursSection />
      
      <StickyBookingButton link="#book-now" />
    </div>
  );
};

export default FinancialDistrict;
